import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFetcher, useRouteLoaderData } from '@remix-run/react';
import { type TipJarChipInOptions, TipJarPage, type TipJarStage } from '@shared/dream-components';

import useStripeSession from '~/hooks/useStripeSession';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { TipJarLoaderData } from '~/interfaces/tip_jar';
import type { RootLoaderData } from '~/root';

import { TipJarCheckout, type TipJarCheckoutStatus } from './TipJarCheckout';
import { toTipJarThemeStyle } from './tipJarTheme';
import { useTipJarCopy } from './useTipJarCopy';

// A reload soon after tipping keeps the thanks; a later visit starts a new tip.
const TIPPED_THANKS_TTL_MS = 60 * 60 * 1000;

const TipJarReader = () => {
  const { tipJar, pageUrl, initialAmountCents } = useTypedLoaderData<TipJarLoaderData>();
  const { site } = useRouteLoaderData('root') as RootLoaderData;
  const publication = site.publication;
  const tippedStorageKey = `tip_jar_tipped:${publication.id}`;
  const { t, i18n } = useTranslation();
  const copy = useTipJarCopy(publication.name);
  const fetcher = useFetcher<{ clientSecret: string | null }>();
  const [amountCents, setAmountCents] = useState(initialAmountCents ?? tipJar.preset_amounts_cents[0]);
  const clientSecret = fetcher.data?.clientSecret || null;
  // The latest secret opens checkout. Thanks and changing the amount leave that secret behind, so a response
  // still sitting on the page does not pull the reader back into Stripe.
  const [secretLeftBehind, setSecretLeftBehind] = useState<string | null>(null);
  const [stageAfterLeaving, setStageAfterLeaving] = useState<'choose' | 'thanks'>('choose');
  const checkoutOpen = Boolean(clientSecret) && clientSecret !== secretLeftBehind;
  const stage: TipJarStage = checkoutOpen ? 'checkout' : stageAfterLeaving;
  // Stripe.js and its cookies wait for a reader who chips in, and load while Swarm creates their session.
  const { stripeSession, isLoadingStripeSession } = useStripeSession({
    publication,
    shouldLoadStripe: fetcher.state !== 'idle' || checkoutOpen,
  });
  const [contributedAmountsCents, setContributedAmountsCents] = useState<number[]>([]);
  const [checkoutStatus, setCheckoutStatus] = useState<TipJarCheckoutStatus>('loading');

  useEffect(() => {
    try {
      const tippedAt = Number(localStorage.getItem(tippedStorageKey));
      if (Date.now() - tippedAt < TIPPED_THANKS_TTL_MS) {
        setStageAfterLeaving('thanks');
      } else {
        localStorage.removeItem(tippedStorageKey);
      }
    } catch {}
  }, [tippedStorageKey]);

  const chipIn = (nextAmountCents: number, { coverFee, message }: TipJarChipInOptions) => {
    if (fetcher.state !== 'idle') return;

    setAmountCents(nextAmountCents);
    fetcher.submit({ amount_cents: nextAmountCents, cover_fee: coverFee, message }, { method: 'post' });
  };

  const leaveCheckout = useCallback(
    (nextStage: 'choose' | 'thanks') => {
      setSecretLeftBehind(clientSecret);
      setStageAfterLeaving(nextStage);
      try {
        if (nextStage === 'thanks') localStorage.setItem(tippedStorageKey, String(Date.now()));
        else localStorage.removeItem(tippedStorageKey);
      } catch {}
      setCheckoutStatus('loading');
    },
    [clientSecret, tippedStorageKey],
  );

  // The webhook records the contribution moments later; the meter moves now so the reader sees their part.
  // It counts the tip alone, never a card fee the reader covered.
  const completeCheckout = useCallback(() => {
    setContributedAmountsCents((current) => [...current, amountCents]);
    leaveCheckout('thanks');
  }, [amountCents, leaveCheckout]);

  const raisedCents =
    typeof tipJar.raised_cents === 'number'
      ? contributedAmountsCents.reduce((total, amount) => total + amount, tipJar.raised_cents)
      : undefined;
  const jar = {
    ...tipJar,
    ...(raisedCents === undefined ? {} : { raised_cents: raisedCents }),
    supporters_count: tipJar.supporters_count + contributedAmountsCents.length,
  };
  const hasSessionError = stage === 'choose' && fetcher.state === 'idle' && fetcher.data?.clientSecret === null;
  const hasCheckoutLoadError =
    checkoutOpen && (checkoutStatus === 'failed' || (!isLoadingStripeSession && !stripeSession));
  const isLoadingCheckout = checkoutOpen && !hasCheckoutLoadError && checkoutStatus !== 'ready';
  let errorMessage: string | null = null;
  if (hasSessionError) {
    errorMessage = t('tip_jar.checkout_error');
  } else if (hasCheckoutLoadError) {
    errorMessage = t('tip_jar.checkout_blocked');
  }

  // The page's own theme sits on a wrapper that adds no box, so the navbar and footer keep the site's.
  return (
    <div style={{ display: 'contents', ...toTipJarThemeStyle(tipJar.theme) }}>
      <TipJarPage
        jar={jar}
        publicationName={publication.name}
        publicationLogoUrl={publication.logo?.thumb?.url}
        homeUrl="/"
        shareUrl={pageUrl}
        copy={copy}
        locale={i18n.language}
        stage={stage}
        initialAmountCents={amountCents}
        checkout={
          checkoutOpen && clientSecret && stripeSession && checkoutStatus !== 'failed' ? (
            <TipJarCheckout
              stripe={stripeSession}
              clientSecret={clientSecret}
              onComplete={completeCheckout}
              onStatusChange={setCheckoutStatus}
            />
          ) : null
        }
        isStartingCheckout={fetcher.state !== 'idle'}
        isLoadingCheckout={isLoadingCheckout}
        errorMessage={errorMessage}
        onChipIn={chipIn}
        onChangeAmount={() => leaveCheckout('choose')}
      />
    </div>
  );
};

export default TipJarReader;
